<template>
  <div>
    <v-menu offset-y>
      <template v-slot:activator="{ on }">
        <v-btn
          small
          color="primary"
          class="ml-2"
          :disabled="disabled"
          :loading="loading"
          depressed
          v-on="on"
        >
          {{ $t('workPackages.exportToSAP.title') }}
          <v-icon>mdi-chevron-down</v-icon>
        </v-btn>
      </template>

      <v-list>
        <v-list-item @click="handleExportFile">
          <v-list-item-title>
            {{ $t('workPackages.exportToSAP.downloadFile') }}
          </v-list-item-title>
        </v-list-item>
        <v-list-item @click="handleDownstreamExport">
          <v-list-item-title>
            {{ $t('workPackages.exportToSAP.exportToSystem') }}
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <!-- Downstream system export confirmation dialog -->
    <alert-dialog
      :is-open="isDownstreamExportDialogOpen"
      :ok-btn-text="$t('actions.confirm')"
      :disable-ok-button="!isExportEligible"
      @onOk="confirmDownstreamExport"
      @onCancel="closeDownstreamExportDialog"
    >
      <template v-slot:header>{{ $t('dialogs.exportToDownstreamSystem.header') }}</template>
      <template v-slot:body>
        <p id="downstream-export-warning" class="dialog-warning">
          <strong>{{ $t('dialogs.exportToDownstreamSystem.warning') }}</strong>
        </p>
        <p>
          <span v-if="formattedBodyText.plain">
            {{ formattedBodyText.plain }}
            <strong v-if="formattedBodyText.bold">{{ formattedBodyText.bold }}</strong>
          </span>
        </p>
        <v-alert v-if="!isExportEligible" type="error" outlined>
          {{ $t('workPackages.noApprovedPricingGroups') }}
        </v-alert>
      </template>
    </alert-dialog>
  </div>
</template>

<script>
import to from 'await-to-js';
import AlertDialog from './alert-dialog.vue';

export default {
  components: { AlertDialog },
  props: {
    disabled: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    params: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isDownstreamExportDialogOpen: false,
      downstreamExportWithFTP: false,
      isExportEligible: false,
      isCheckingApproval: false,
    };
  },
  computed: {
    formattedBodyText() {
      const text = this.$t('dialogs.exportToDownstreamSystem.body', {
        workpackageDescription: this.params.selectedWorkpackage
          ? this.params.selectedWorkpackage.description
          : '',
      });
      return this.parseFormattedText(text);
    },
  },
  methods: {
    /**
     * Handle download file option - emits export without confirmation.
     */
    handleExportFile() {
      this.$emit('downloadExportToSAP', {
        ...this.params,
        toFTP: false,
      });
    },

    /**
     * Handle downstream system export - opens confirmation dialog immediately and validates approval status.
     * Downstream exports (FTP, XSR, etc.) require user confirmation due to system impact.
     * Shows a spinner whilst checking approval status.
     */
    async handleDownstreamExport() {
      this.isDownstreamExportDialogOpen = true;
      this.downstreamExportWithFTP = true;
      this.isCheckingApproval = true;
      await this.checkExportApprovalStatus();
      this.isCheckingApproval = false;
    },

    /**
     * Check if workpackage has approved pricing groups for export.
     */
    async checkExportApprovalStatus() {
      const { selectedWorkpackage } = this.params;
      if (!selectedWorkpackage) return;

      const [err, response] = await to(
        this.$http.get(
          `/api/work-packages/workpackage/${selectedWorkpackage._id}/check-export-approval-status`
        )
      );

      if (err) {
        console.error('Error checking export approval status', err);
        this.isExportEligible = false; // Deny export if check fails
        return;
      }

      this.isExportEligible = response.data.eligible;
    },

    /**
     * Confirm downstream export and emit event with FTP flag set to true.
     */
    confirmDownstreamExport() {
      if (!this.isExportEligible) return;
      this.isDownstreamExportDialogOpen = false;
      this.$emit('downloadExportToSAP', {
        ...this.params,
        toFTP: this.downstreamExportWithFTP,
      });
    },

    /**
     * Close downstream export confirmation dialog without exporting.
     */
    closeDownstreamExportDialog() {
      this.isDownstreamExportDialogOpen = false;
      this.downstreamExportWithFTP = false;
      this.isExportEligible = false; // Reset for next dialog open
    },

    /**
     * Parse formatted text into components (extracts bold text marked with **text**).
     * @param {string} text - The text to parse.
     * @returns {Object} Object with plain and bold text parts.
     */
    parseFormattedText(text) {
      const match = text.match(/^(.*?)\*\*(.*?)\*\*(.*)$/);
      if (match) {
        return {
          plain: match[1],
          bold: match[2],
        };
      }
      return { plain: text, bold: null };
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.dialog-warning {
  color: $pricing-red;
}
</style>
